import React, { useState, useEffect, useMemo } from 'react';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell,
} from 'recharts';
import { useTranslation } from 'react-i18next';
import { getGGRReport, getDepositReport, getWithdrawalReport, getActivePlayers } from '../api/client';
import { SiteFilter, siteParam, siteName, useSites } from '../components/SiteFilter';
import { CurrencySelector, type CurrencyMode, currencySymbol } from '../components/CurrencySelector';
import { formatAmount, formatNumber } from '../lib/format';
import { metricTip } from '../lib/metric-info';
import { Th } from '../components/Th';

// Per-site aggregate of the main KPIs.
interface SiteAgg {
  site_id: number;
  ggr: number;
  ngr: number;
  turnover: number;
  deposits: number;
  withdrawals: number;
  active_sum: number;
  active_days: number;
  new_players: number;
}

interface SiteKPIs {
  site_id: number;
  ggr: number;
  ngr: number;
  turnover: number;
  deposits: number;
  withdrawals: number;
  net_cash: number;
  active: number; // avg active players / day
  new_players: number;
  arpu: number;
  hold_pct: number;
  ggr_margin: number;
}

type Fmt = 'money' | 'count' | 'pct';
interface KpiDef { key: keyof SiteKPIs; labelKey: string; fallback: string; fmt: Fmt; higherBetter: boolean; info: string }

const KPIS: KpiDef[] = [
  { key: 'ggr', labelKey: 'siteComparison.ggr', fallback: 'GGR', fmt: 'money', higherBetter: true, info: 'ggr' },
  { key: 'ngr', labelKey: 'siteComparison.ngr', fallback: 'NGR', fmt: 'money', higherBetter: true, info: 'ngr' },
  { key: 'turnover', labelKey: 'siteComparison.turnover', fallback: 'Turnover', fmt: 'money', higherBetter: true, info: 'turnover' },
  { key: 'deposits', labelKey: 'siteComparison.deposits', fallback: 'Deposits', fmt: 'money', higherBetter: true, info: 'deposits' },
  { key: 'withdrawals', labelKey: 'siteComparison.withdrawals', fallback: 'Withdrawals', fmt: 'money', higherBetter: false, info: 'withdrawals' },
  { key: 'net_cash', labelKey: 'siteComparison.netCash', fallback: 'Net cash', fmt: 'money', higherBetter: true, info: 'netCash' },
  { key: 'active', labelKey: 'siteComparison.activePlayers', fallback: 'Active / day', fmt: 'count', higherBetter: true, info: 'activePerDay' },
  { key: 'new_players', labelKey: 'siteComparison.newPlayers', fallback: 'New players', fmt: 'count', higherBetter: true, info: 'newPlayers' },
  { key: 'arpu', labelKey: 'siteComparison.arpu', fallback: 'ARPU', fmt: 'money', higherBetter: true, info: 'arpu' },
  { key: 'hold_pct', labelKey: 'siteComparison.holdPct', fallback: 'Hold %', fmt: 'pct', higherBetter: true, info: 'holdPct' },
  { key: 'ggr_margin', labelKey: 'siteComparison.ggrMargin', fallback: 'GGR margin', fmt: 'pct', higherBetter: true, info: 'ggrMargin' },
];

function num(v: unknown): number { return Number(v) || 0; }

export const SiteComparison: React.FC = () => {
  const { t } = useTranslation();
  const { allSites } = useSites();
  const [loading, setLoading] = useState(false);
  const [rows, setRows] = useState<SiteKPIs[]>([]);
  const [metric, setMetric] = useState<keyof SiteKPIs>('ggr');
  const [dateFrom, setDateFrom] = useState(() => {
    const d = new Date();
    return new Date(d.getFullYear(), d.getMonth(), 1).toISOString().split('T')[0];
  });
  const [dateTo, setDateTo] = useState(() => new Date().toISOString().split('T')[0]);
  const [siteIds, setSiteIds] = useState<number[]>([]);
  const [currencyMode, setCurrencyMode] = useState<CurrencyMode>('EUR');
  const symbol = currencySymbol(currencyMode);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    const sp = siteParam(allSites, siteIds);
    const dc = currencyMode === 'BASE' ? 'EUR' : currencyMode;
    Promise.all([
      getGGRReport({ date_from: dateFrom, date_to: dateTo, site_ids: sp, display_currency: dc }).catch(() => ({ data: [] })),
      getDepositReport({ date_from: dateFrom, date_to: dateTo, site_ids: sp, display_currency: dc }).catch(() => ({ data: [] })),
      getWithdrawalReport({ date_from: dateFrom, date_to: dateTo, site_ids: sp, display_currency: dc }).catch(() => ({ data: [] })),
      getActivePlayers({ date_from: dateFrom, date_to: dateTo, site_ids: sp }).catch(() => ({ data: [] })),
    ]).then(([ggr, dep, wd, act]) => {
      if (cancelled) return;
      const agg = new Map<number, SiteAgg>();
      const get = (id: number): SiteAgg => {
        let a = agg.get(id);
        if (!a) { a = { site_id: id, ggr: 0, ngr: 0, turnover: 0, deposits: 0, withdrawals: 0, active_sum: 0, active_days: 0, new_players: 0 }; agg.set(id, a); }
        return a;
      };
      for (const r of (ggr.data || [])) { const a = get(num(r.site_id)); a.ggr += num(r.ggr); a.ngr += num(r.ngr); a.turnover += num(r.turnover); }
      for (const r of (dep.data || [])) { get(num(r.site_id)).deposits += num(r.total_deposits ?? r.amount); }
      for (const r of (wd.data || [])) { get(num(r.site_id)).withdrawals += num(r.total_withdrawals ?? r.amount); }
      for (const r of (act.data || [])) { const a = get(num(r.site_id)); a.active_sum += num(r.active_players); a.active_days += 1; a.new_players += num(r.new_players); }

      const result: SiteKPIs[] = Array.from(agg.values()).map((a) => {
        const net = a.deposits - a.withdrawals;
        const active = a.active_days > 0 ? a.active_sum / a.active_days : 0;
        return {
          site_id: a.site_id,
          ggr: a.ggr, ngr: a.ngr, turnover: a.turnover,
          deposits: a.deposits, withdrawals: a.withdrawals, net_cash: net,
          active, new_players: a.new_players,
          arpu: active > 0 ? a.ngr / active : 0,
          hold_pct: a.deposits > 0 ? (net / a.deposits) * 100 : 0,
          ggr_margin: a.turnover > 0 ? (a.ggr / a.turnover) * 100 : 0,
        };
      });
      setRows(result);
    }).finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dateFrom, dateTo, siteIds.join(','), currencyMode]);

  const fmtVal = (v: number, fmt: Fmt) =>
    fmt === 'money' ? `${symbol}${formatAmount(v)}` : fmt === 'pct' ? `${formatNumber(v, 1)}%` : formatNumber(v);

  const activeKpi = KPIS.find((k) => k.key === metric)!;

  const sorted = useMemo(
    () => [...rows].sort((a, b) => (b[metric] as number) - (a[metric] as number)),
    [rows, metric],
  );
  const leaderId = sorted.length ? sorted[0].site_id : -1;

  const chartData = sorted.map((r) => ({ name: siteName(allSites, r.site_id), value: r[metric] as number, site_id: r.site_id }));

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-bold text-slate-900 dark:text-white">{t('siteComparison.title', 'Site Comparison')}</h1>
        <p className="mt-1 text-slate-600 dark:text-slate-400">{t('siteComparison.subtitle', 'Compare the main KPIs across your sites / brands')}</p>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap items-end gap-4 rounded-lg border border-slate-200 bg-white p-4 dark:border-slate-700 dark:bg-slate-800">
        <div>
          <label className="block text-sm font-medium text-slate-700 dark:text-slate-300">{t('filters.from')}</label>
          <input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)}
            className="mt-1 rounded-md border border-slate-300 bg-white px-3 py-2 text-sm dark:border-slate-600 dark:bg-slate-700 dark:text-white" />
        </div>
        <div>
          <label className="block text-sm font-medium text-slate-700 dark:text-slate-300">{t('filters.to')}</label>
          <input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)}
            className="mt-1 rounded-md border border-slate-300 bg-white px-3 py-2 text-sm dark:border-slate-600 dark:bg-slate-700 dark:text-white" />
        </div>
        <SiteFilter selected={siteIds} onChange={setSiteIds} />
        <CurrencySelector selected={currencyMode} onChange={setCurrencyMode} />
      </div>

      {/* Comparison chart */}
      <div className="rounded-lg border border-slate-200 bg-white p-6 dark:border-slate-700 dark:bg-slate-800">
        <div className="mb-4 flex flex-col justify-between gap-3 sm:flex-row sm:items-center">
          <h2 className="text-lg font-semibold text-slate-900 dark:text-white">{t('siteComparison.chartTitle', 'KPI comparison by site')}</h2>
          <div className="flex items-center gap-2">
            <label className="text-sm text-slate-500 dark:text-slate-400">{t('siteComparison.metric', 'Metric')}</label>
            <select value={metric} onChange={(e) => setMetric(e.target.value as keyof SiteKPIs)}
              className="rounded-md border border-slate-300 bg-white px-3 py-1.5 text-sm dark:border-slate-600 dark:bg-slate-700 dark:text-white">
              {KPIS.map((k) => <option key={k.key} value={k.key}>{t(k.labelKey, k.fallback)}</option>)}
            </select>
          </div>
        </div>
        {chartData.length === 0 ? (
          <p className="py-16 text-center text-slate-500">{loading ? t('filters.loading') : t('siteComparison.noData', 'No data for the selected period')}</p>
        ) : (
          <ResponsiveContainer width="100%" height={340}>
            <BarChart data={chartData} layout="vertical" margin={{ left: 24 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
              <XAxis type="number" stroke="#9ca3af" style={{ fontSize: '12px' }}
                tickFormatter={(v: number) => activeKpi.fmt === 'pct' ? `${formatNumber(v, 0)}%` : activeKpi.fmt === 'money' ? `${symbol}${formatNumber(v)}` : formatNumber(v)} />
              <YAxis type="category" dataKey="name" stroke="#9ca3af" width={90} style={{ fontSize: '12px' }} />
              <Tooltip contentStyle={{ backgroundColor: '#1e293b', border: '1px solid #475569', borderRadius: '8px', color: '#fff' }}
                formatter={(v: number) => fmtVal(v, activeKpi.fmt)} />
              <Bar dataKey="value" name={t(activeKpi.labelKey, activeKpi.fallback)} radius={[0, 4, 4, 0]}>
                {chartData.map((d) => <Cell key={d.site_id} fill={d.site_id === leaderId ? '#009EA0' : '#94a3b8'} />)}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        )}
      </div>

      {/* Comparison table — sites as rows, KPIs as columns; leader on the active metric highlighted */}
      <div className="overflow-x-auto rounded-lg border border-slate-200 dark:border-slate-700">
        <table className="w-full text-left text-sm">
          <thead className="bg-slate-50 dark:bg-slate-800">
            <tr>
              <Th tip={metricTip('site')}>{t('siteComparison.site', 'Site')}</Th>
              {KPIS.map((k) => (
                <Th key={k.key} align="right" tip={metricTip(k.info)} className={k.key === metric ? 'text-accent' : ''}>
                  {t(k.labelKey, k.fallback)}
                </Th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-200 dark:divide-slate-700">
            {sorted.length === 0 ? (
              <tr><td colSpan={KPIS.length + 1} className="px-4 py-8 text-center text-slate-500">{loading ? t('filters.loading') : t('siteComparison.noData', 'No data for the selected period')}</td></tr>
            ) : sorted.map((r) => (
              <tr key={r.site_id} className="bg-white dark:bg-slate-900">
                <td className="px-4 py-3 font-medium text-slate-900 dark:text-white">{siteName(allSites, r.site_id)}</td>
                {KPIS.map((k) => {
                  const v = r[k.key] as number;
                  const isLeader = k.key === metric && r.site_id === leaderId;
                  return (
                    <td key={k.key} className={`px-4 py-3 text-right font-mono ${isLeader ? 'font-bold text-accent' : (v < 0 && k.fmt === 'money' ? 'text-red-600 dark:text-red-400' : 'text-slate-900 dark:text-white')}`}>
                      {fmtVal(v, k.fmt)}
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};
